import { AxiosRequestConfig } from 'axios';
import { HttpResponse, ajax } from '../../../../common/ajax';
import { config } from '../../config';

import moment, { Moment } from 'moment';

import {
  File,
  PresenceTypes,
  Timesheet,
  TimesheetBankHolidays,
} from '../../models/TimeAndAttendance';
import { AttendancecurrentAction } from '../../Pages/MyAccount/TimeAndAttendencePage/AttendenceCheckInPage/AttendanceCheckIn/common';

export interface ITimeAndAttendanceService {
  getTimesheet: (
    selectedMonth: Moment,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<Timesheet[]>>;
  getTimesheetFiles: (
    selectedMonth: Moment,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<File[]>>;
  getTimesheetPresenceTypes: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<PresenceTypes[]>>;
  addTimesheetPresenceEvent: (
    event: Timesheet,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  updateTimesheetPresenceEvent: (
    eventId: string,
    event: Timesheet,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  removeTimesheetPresenceEvent: (
    eventId: string,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  getTimesheetBankHolidays: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<TimesheetBankHolidays[]>>;
  attendanceCheckIn: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  attendanceCheckOut: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  getCheckinStatus: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<AttendancecurrentAction>>;
  submitTimesheet: (
    selectedMonth: Moment,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  downloadTimesheetPresenceFile: (
    uploadId: number,
    filename: string,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<null>>;
  removeTimesheetPresenceFile: (
    uploadId: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
}

export const timeAndAttendanceService: ITimeAndAttendanceService = {
  getTimesheetPresenceTypes(axiosConfig) {
    return ajax.get<PresenceTypes[]>(
      `${config.benefitApiUrl}/my-timesheet-presence-types`,
      axiosConfig,
    );
  },
  getTimesheetFiles(selectedMonth, axiosConfig) {
    return ajax.get<File[]>(
      `${
        config.benefitApiUrl
      }/timesheet-presences/files?month=${selectedMonth.format('YYYY-MM-DD')}`,
      axiosConfig,
    );
  },
  getTimesheet(selectedMonth, axiosConfig) {
    return ajax.get<Timesheet[]>(
      `${config.benefitApiUrl}/timesheet-presences?month=${selectedMonth.format(
        'YYYY-MM-DD',
      )}`,
      axiosConfig,
    );
  },

  addTimesheetPresenceEvent(event, axiosConfig) {
    const formData = new FormData();

    event.files
      .filter(
        file => !file.validationErrors || file.validationErrors?.length == 0,
      )
      .forEach(file => {
        if (file.getRawFile)
          formData.append('files', file.getRawFile(), file.name);
      });

    formData.append(
      'workDataStartDate',
      moment(event.startDate).format('YYYY-MM-DD'),
    );

    formData.append(
      'workDataEndDate',
      moment(event.endDate).format('YYYY-MM-DD'),
    );

    formData.append(
      'presenceTypeId',
      event.presenceType?.presenceTypeId?.toString() ?? '',
    );

    formData.append('startWorkHour', event.workEvent.startWorkHour);
    formData.append('endWorkHour', event.workEvent.endWorkHour);
    formData.append('startBreakHour', event.workEvent.startBreakHour);
    formData.append('endBreakHour', event.workEvent.endBreakHour);
    formData.append(
      'presenceTypeCategory',
      event.presenceType?.categoryId.toString() ?? '',
    );

    return ajax.post<boolean>(
      `${config.benefitApiUrl}/timesheet-presences`,
      formData,
      axiosConfig,
    );
  },
  updateTimesheetPresenceEvent(eventId, event, axiosConfig) {
    return ajax.put<boolean>(
      `${config.benefitApiUrl}/timesheet-presences/${eventId}`,
      {
        id: event.id,
        workDataStartDate: moment(event.startDate).format('YYYY-MM-DD'),
        workDataEndDate: moment(event.startDate).format('YYYY-MM-DD'),
        presenceTypeId: event.presenceType?.presenceTypeId,
        startWorkHour: event.workEvent.startWorkHour,
        endWorkHour: event.workEvent.endWorkHour,
        startBreakHour: event.workEvent.startBreakHour,
        endBreakHour: event.workEvent.endBreakHour,
        presenceTypeCategory: event.presenceType?.categoryId,
      },
      axiosConfig,
    );
  },
  removeTimesheetPresenceEvent(eventId, axiosConfig) {
    return ajax.delete<boolean>(
      `${config.benefitApiUrl}/timesheet-presences/${eventId}`,
      axiosConfig,
    );
  },
  getTimesheetBankHolidays(axiosConfig) {
    return ajax.get<TimesheetBankHolidays[]>(
      `${config.benefitApiUrl}/timesheet-bank-holidays`,
      axiosConfig,
    );
  },
  attendanceCheckIn(axiosConfig) {
    return ajax.post<boolean>(
      `${config.benefitApiUrl}/timesheet-presences/check-in`,
      axiosConfig,
    );
  },
  attendanceCheckOut(axiosConfig) {
    return ajax.post<boolean>(
      `${config.benefitApiUrl}/timesheet-presences/check-out`,
      axiosConfig,
    );
  },
  getCheckinStatus(axiosConfig) {
    return ajax.get<AttendancecurrentAction>(
      `${config.benefitApiUrl}/timesheet-presences/get-checkin-status`,
      axiosConfig,
    );
  },
  submitTimesheet(selectedMonth, axiosConfig) {
    return ajax.post<boolean>(
      `${
        config.benefitApiUrl
      }/timesheet-presences/submit?month=${selectedMonth.format('YYYY-MM-DD')}`,
      axiosConfig,
    );
  },
  removeTimesheetPresenceFile(uploadId, axiosConfig) {
    return ajax.delete<boolean>(
      `${config.benefitApiUrl}/timesheet-presences/files/${uploadId}`,
      axiosConfig,
    );
  },
  downloadTimesheetPresenceFile(uploadId, filename, axiosConfig) {
    return ajax.downloadFile(
      `${config.benefitApiUrl}/timesheet-presences/files/${uploadId}`,
      filename,
      'pdf',
      axiosConfig,
    );
  },
};
